import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom"
import { Dropdown, Button } from "antd"
import { UserOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next";
import { switchAccount } from "auth";
import { availableAccountsForUser } from "api/services/authServices";
import { APP_MODULE_KEYS } from "modules/appModuleKeys";
import { canAccessModule } from "modules/appModuleAccess";
import useAuthStore from "stores/useAuthStore";

const UserMenu = ({ onLogout, customTriggerRender }) => {
  const { t, i18n } = useTranslation(["common"])
  const initialAuthToken = useAuthStore((s) => s.initialAuthToken);
  const initialUserData = useAuthStore((s) => s.initialUserData);
  const userData = useAuthStore((s) => s.userData);
  const navigate = useNavigate();

  const [availableAccounts, setAvailableAccounts] = useState(null);

  const isInitialUserOwner = initialUserData?.role === "owner";
  // check if the user can access the configuration module
  const canAccessConfiguration = canAccessModule(APP_MODULE_KEYS.CONFIGURATION, userData);

  const CSS_Selected_Role = `rgba(174, 214, 241, 0.6)`;

  const roleLabels = useMemo(() => ({
    "current_user": t("common:roles.current_user"),
    "owner": t("common:roles.owner"),
    "manager": t("common:roles.manager"),
    "user": t("common:roles.user"),
    "evaluator": t("common:roles.evaluator"),
  }), [i18n.language]);

  const fetchUserAccounts = async () => {
    try {
      const response = await availableAccountsForUser();
      const parsedResponse = (typeof response === "object" && !Array.isArray(response) && response !== null) ? response : {};

      setAvailableAccounts((prev) => ({
        ...prev,
        ...parsedResponse
      }));
    } catch (error) {
      console.error("Couldn't fetch additional accounts: ", error);
    }
  };

  useEffect(() => {
    if (initialAuthToken && initialUserData) {
      setAvailableAccounts({
        active_user: {
          email: initialUserData.sub,
          access_token: initialAuthToken
        }
      });

      if (isInitialUserOwner) fetchUserAccounts();
    } else {
      setAvailableAccounts(null);
    }
  }, [initialAuthToken, initialUserData, isInitialUserOwner]);

  const handleSettingsClick = () => {
    navigate("/configuration")
  };

  const items = useMemo(() => {
    return [
      ...(canAccessConfiguration
        ? [{
          key: "config",
          label: (
            <span style={{ display: "inline-flex", alignItems: "center" }}>
              {t("common:buttons.configuration")}
            </span>
          ),
          onClick: handleSettingsClick,
        },
        { type: "divider", key: "div-1" },
        ] : []
      ),

      // Impersonation options
      ...(availableAccounts
        ? [
          ...(availableAccounts?.active_user ? [{
            key: "active_user",
            label: (
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                {availableAccounts.active_user?.email || roleLabels["current_user"]}
              </span>
            ),
            onClick: () => {
              switchAccount(availableAccounts.active_user?.access_token);
              window.location.reload();
            },
            style: availableAccounts.active_user?.email === userData?.email
              ? { backgroundColor: CSS_Selected_Role }
              : {},
          }] : []),
          ...((availableAccounts?.owner && !availableAccounts?.owner?.message) ? [{
            key: "owner",
            label: (
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                {roleLabels["owner"]}
              </span>
            ),
            onClick: () => {
              switchAccount(availableAccounts.owner?.access_token);
              window.location.reload();
            },
            style: availableAccounts.owner?.email === userData?.email
              ? { backgroundColor: CSS_Selected_Role }
              : {},
          }] : []),
          ...((availableAccounts?.admin && !availableAccounts?.admin?.message) ? [{
            key: "admin",
            label: (
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                {roleLabels["manager"]}
              </span>
            ),
            onClick: () => {
              switchAccount(availableAccounts.admin?.access_token);
              window.location.reload();
            },
            style: availableAccounts.admin?.email === userData?.email
              ? { backgroundColor: CSS_Selected_Role }
              : {},
          }] : []),
          ...((availableAccounts?.user && !availableAccounts?.user?.message) ? [{
            key: "user",
            label: (
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                {roleLabels["user"]}
              </span>
            ),
            onClick: () => {
              switchAccount(availableAccounts.user?.access_token);
              window.location.reload();
            },
            style: availableAccounts.user?.email === userData?.email
              ? { backgroundColor: CSS_Selected_Role }
              : {},
          }] : []),
          ...((availableAccounts?.evaluator && !availableAccounts?.evaluator?.message) ? [{
            key: "evaluator",
            label: (
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                {roleLabels["evaluator"]}
              </span>
            ),
            onClick: () => {
              switchAccount(availableAccounts.evaluator?.access_token);
              window.location.reload();
            },
            style: availableAccounts.evaluator?.email === userData?.email
              ? { backgroundColor: CSS_Selected_Role }
              : {},
          }] : []),
          { type: "divider", key: "div-2" },
        ] : []
      ),

      // Always show logout button
      {
        key: "logout",
        label: (
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            {t("common:buttons.logout")}
          </span>
        ),
        onClick: onLogout,
      },
    ]
  }, [i18n.language, canAccessConfiguration, availableAccounts, roleLabels]);


  return (
    <Dropdown
      menu={{ items }}
      trigger={["click"]}
      placement="bottomRight"
    >
      {customTriggerRender ?? (
        <Button type="link" style={{ color: "#797979" }}>
          <UserOutlined style={{ fontSize: 20 }} />
        </Button>
      )}
    </Dropdown>
  )
};

export default UserMenu